/** MI DOMINO - MI DASHBOARD v2: the player hub (profile · ranks · shop ·
 *  points · PRO club · collection · market · purchases · team · edit ·
 *  history) and the Founder's admin panel (users, sales, chat moderation,
 *  client errors) — one screen per tab, rendered into #dash-body. */
import { exportAdminCSV, loadLeaderboard, renderEnroll, saveAdminDB } from '../admin/portal';
import { BUNDLES, COLLECTIBLES, FLAG_LIST, PACK_PRICE, RARITY, SELL_VALUES, STORE, iname } from '../data/catalog';
import { DASH_TABS, IS_FOUNDER, MARKET_FEE, ONLINE_MODE, WOOD_SW } from '../data/config';
import { questsHTML } from '../economy/quests';
import { achievementsHTML } from '../identity/achievements';
import { buyBundle, buyPack, buyPro, claimMonthlyDrop, colTileHTML, isPro, payLabel, sellCollectible, tpItemCard, tpToggle } from '../economy/tienda';
import { L } from '../game/engine';
import { clack } from '../game/sounds';
import { applyProfile, saveAccount, teamCode, upsertSelfUser } from '../identity/profile';
import { RANKS, RANK_COLORS, RANK_SPLIT, rankInfo } from '../identity/ranks';
import { api } from '../net/api';
import { ACCOUNT, ADMIN_DB, API_TOKEN, MATCH_LOG, PROFILE, STATS, TEAM, WALLET, adminUserSel, dashTab, dashView, setAdminUserSel, setDashTab, setDashView } from '../state';
import { doLogout } from './account';
import { $, $$ } from './dom';
import { log } from './feed';
import { LANG, t } from './i18n';

let ADMIN_CHATS: any[] = [];  // recent chat log for the founder's moderation panel
let ADMIN_ERRORS: any[] = []; // client error reports (window.onerror → /api/client-error)
let ADMIN_RESETS: any = null; // pending password-reset codes { resets, mail } (support hands them out)

/** Domino pips for one half (0–6), laid out on a 3×3 grid. */
function domPips(n: number): string {
  const P: Record<number, [number, number][]> = { 0: [], 1: [[1, 1]], 2: [[0, 0], [2, 2]], 3: [[0, 0], [1, 1], [2, 2]], 4: [[0, 0], [0, 2], [2, 0], [2, 2]], 5: [[0, 0], [0, 2], [1, 1], [2, 0], [2, 2]], 6: [[0, 0], [0, 1], [0, 2], [2, 0], [2, 1], [2, 2]] };
  const on = P[n] || [];
  let h = '<div class="rk-pips">';
  for (let rr = 0; rr < 3; rr++) for (let c = 0; c < 3; c++) h += on.some(p => p[0] === rr && p[1] === c) ? '<i class="rk-pip"></i>' : '<i></i>';
  return h + '</div>';
}

/** One rank section: colored bar, domino badge whose halves add up to the rank number, name, XP. */
function rankTileHTML(i: number, cur: boolean, locked: boolean): string {
  const [t, b] = RANK_SPLIT[i];
  const col = RANK_COLORS[i];
  const lvl = i + 1;
  const name = LANG === 'en' ? RANKS[i][2] : RANKS[i][1];
  const cls = ['rk-tile', lvl === 10 ? 'dark' : '', lvl >= 7 ? 'glow' : '', lvl >= 9 ? 'frame2' : lvl >= 5 ? 'frame3' : ''].join(' ');
  const crown = lvl >= 7 ? `<div class="rk-crown">${lvl >= 10 ? '👑' : '♛'}</div>` : '';
  const gem = lvl >= 8 ? `<i class="rk-gem" style="background:${col}"></i>` : '';
  return `<div class="rk-sec${cur ? ' cur' : ''}" style="opacity:${locked ? .5 : 1};border-color:${cur ? col : 'transparent'}">
    <div class="rk-bar" style="background:${col}">${lvl} · ${name.toUpperCase()}</div>
    <div class="rk-body">
      <img class="rk-art" src="assets/ranks/r${lvl}.png" alt="${RANKS[i][1]}">
      <div class="rk-name" style="color:${col}">${RANKS[i][1]}</div>
      <div class="rk-xp">${RANKS[i][3]} XP</div>
      ${cur ? `<div class="rk-here" style="background:${col}">${LANG === 'en' ? 'YOU ARE HERE' : 'AQUÍ ESTÁS'}</div>` : ''}
    </div></div>`;
}

/** Render the current dashboard view into #dash-body and wire its buttons. */
export function renderDash(): void {
  const r = rankInfo();
  const winRate = STATS.matches ? Math.round(STATS.matchesWon / STATS.matches * 100) : 0;
  let loginStreak = 0;
  try { loginStreak = parseInt(localStorage.getItem('mddailystreak') || '0', 10) || 0; } catch (e) {}
  // admin → founder only · edit profile → signed-in only (guests have no account to edit)
  const tabsHtml = `<div class="tp-tabs">${DASH_TABS.filter(([id]) => (id !== 'admin' || IS_FOUNDER) && (id !== 'editar' || ACCOUNT.email)).map(([id, es, en]) =>
    `<button class="${dashView === id ? 'active' : ''}" data-dview="${id}" ${id === 'admin' ? 'style="border:1px solid var(--gold)"' : ''}>${LANG === 'en' ? en : es}</button>`).join('')}</div>`;
  let body = '';

  if (dashView === 'perfil') {
    body = `<div class="perfil-grid" style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));grid-auto-rows:auto;gap:14px;align-items:stretch;max-width:1150px">
    <div class="tp-card" style="text-align:left;grid-column:1;grid-row:1">
      <div style="display:flex;align-items:center;gap:14px">
        ${PROFILE.photo
          ? `<img src="${PROFILE.photo}" style="width:68px;height:68px;border-radius:50%;object-fit:cover;border:3px solid var(--gold)">`
          : `<span style="font-size:56px">${PROFILE.avatar}</span>`}
        <div style="flex:1;min-width:0">
          <b style="font-size:20px">${PROFILE.country} ${PROFILE.name || (ACCOUNT.email ? (LANG === 'en' ? 'Player' : 'Jugador') : t('guest'))}</b><br>
          <small style="opacity:.75">${TEAM.name ? TEAM.flag + ' ' + TEAM.name : t('sinEquipo')}${TEAM.motto ? ' · "' + TEAM.motto + '"' : ''}</small><br>
          ${IS_FOUNDER ? `<span style="display:inline-block;margin-top:6px;margin-right:6px;background:linear-gradient(135deg,#b8860b,#ffd700,#b8860b);color:#241a02;padding:3px 10px;border-radius:12px;font-size:11px;font-weight:800;letter-spacing:.5px">🎖️ ${LANG === 'en' ? 'FOUNDER · Creator of MI DOMINO' : 'FUNDADOR · Creador de MI DOMINO'}</span>` : ''}
          ${ACCOUNT.email ? '' : `<span style="display:inline-block;margin-top:6px;margin-right:6px;background:rgba(255,255,255,.08);border:1px dashed rgba(255,255,255,.3);padding:3px 10px;border-radius:12px;font-size:11px;font-weight:700">👤 ${LANG === 'en' ? 'Guest — sign in to keep your coins, stats & purchases' : 'Invitado — entra para guardar tus monedas, stats y compras'}</span>`}
          <span id="dash-pro-chip" style="display:inline-block;margin-top:6px;background:${isPro() ? 'var(--gold)' : 'var(--accent)'};color:${isPro() ? '#241a02' : '#fff'};padding:3px 10px;border-radius:12px;font-size:11px;font-weight:800;cursor:pointer">${isPro() ? '👑 PRO MEMBER' : t('hazte')}</span>
        </div>
        <div style="text-align:center">
          <div style="font-size:24px;font-weight:800;color:var(--gold)">${WALLET.coins}</div>
          <small style="opacity:.7">🪙 PhilCoins</small>
        </div>
      </div>
      <div style="margin-top:14px">
        <div style="display:flex;justify-content:space-between;font-size:13px">
          <b>${r.emoji} ${LANG === 'en' ? r.eng : r.name} <small style="opacity:.55;font-weight:400">(${LANG === 'en' ? r.name : r.eng})</small>${isPro() ? '' : ' 🔒'}</b>
          <span style="opacity:.7">${r.next ? `${r.xp} XP → ${r.next[0]} ${LANG === 'en' ? r.next[2] : r.next[1]} (${r.next[3]} XP)` : `${r.xp} XP · ${L('¡KING KONG!', 'KING KONG!')} 🦍`}</span>
        </div>
        <div class="dash-bar"><div style="width:${r.pct}%"></div></div>
        <div style="display:flex;justify-content:space-between;margin-top:6px">
          ${RANKS.map((R, i) => `<span title="${LANG === 'en' ? R[2] : R[1]} · ${R[3]} XP" style="font-size:${i === r.idx ? 24 : 15}px;opacity:${i <= r.idx ? 1 : .3}">${R[0]}</span>`).join('')}
        </div>
      </div>
      <div style="display:flex;gap:8px;margin-top:12px">
        <button class="btn gold" id="dash-play" style="flex:1;margin:0">${t('play')}</button>
        ${ACCOUNT.email ? `<button class="btn" id="dash-perfil" style="flex:1;margin:0;background:#2456c9">${t('edit')}</button>` : ''}
        ${ACCOUNT.email
          ? `<button class="btn" id="dash-logout" style="flex:0 0 auto;width:auto;margin:0;padding:8px 14px;background:#7e2b2b" title="${LANG === 'en' ? 'Log out' : 'Cerrar sesión'}">🚪 ${LANG === 'en' ? 'LOG OUT' : 'SALIR'}</button>`
          : `<button class="btn" id="dash-login" style="flex:0 0 auto;width:auto;margin:0;padding:8px 14px;background:#0f6e56">🔑 ${LANG === 'en' ? 'SIGN IN' : 'ENTRAR'}</button>`}
      </div>
    </div>
    <div class="tp-card" style="text-align:left;grid-column:2;grid-row:1 / span 2">
      <b style="font-size:15px">${t('myNumbers')}</b>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:10px">
        <div class="tp-metric"><div class="v">${winRate}%</div><div class="l">${t('winRate')}</div></div>
        <div class="tp-metric"><div class="v">${STATS.matchesWon}/${STATS.matches}</div><div class="l">${t('partidas')}</div></div>
        <div class="tp-metric"><div class="v">${STATS.handsWon}/${STATS.hands}</div><div class="l">${t('manos')}</div></div>
        <div class="tp-metric"><div class="v">${STATS.capicus}</div><div class="l">${t('capicusL')}</div></div>
        <div class="tp-metric"><div class="v">${STATS.chuchazos}</div><div class="l">${t('chuchazosL')}</div></div>
        <div class="tp-metric"><div class="v">${STATS.tranquesWon}</div><div class="l">${t('tranquesL')}</div></div>
        <div class="tp-metric"><div class="v">🔥${STATS.streak}</div><div class="l">${t('rachaAct')}</div></div>
        <div class="tp-metric"><div class="v">${STATS.bestStreak}</div><div class="l">${t('mejorRacha')}</div></div>
        <div class="tp-metric"><div class="v">${r.xp}</div><div class="l">${t('xpTotal')}</div></div>
        <div class="tp-metric"><div class="v">🎁${loginStreak}</div><div class="l">${t('loginStreak')}</div></div>
      </div>
    </div>
    <div class="tp-card" style="text-align:left;grid-column:3;grid-row:1">${questsHTML()}</div>
    <div class="tp-card" style="text-align:left;grid-column:1;grid-row:2">${achievementsHTML()}</div>
    </div>`;
  } else if (dashView === 'rangos') {
    body = `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:14px;align-items:start;max-width:1180px">
    <div class="tp-card" style="text-align:left;grid-column:1/-1">
      <b style="font-size:14px;color:var(--gold)">${LANG === 'en' ? '🏅 RANK SCALE — how far can you climb?' : '🏅 ESCALA DE RANGOS — ¿hasta dónde llegas?'}</b>
      <div class="rk-grid">${RANKS.map((_, i) => rankTileHTML(i, i === r.idx, i > r.idx)).join('')}</div>
    </div>
    <div class="tp-card" style="text-align:left">
      <b style="font-size:15px">${t('howXP')}</b>
      <p style="font-size:13px;color:var(--gold);font-weight:600;margin:8px 0 0">${LANG === 'en' ? 'Simple: every hand won, special play, and victory pays XP — stack XP to climb the 10 ranks.' : 'Simple: cada mano ganada, jugada especial y victoria paga XP — acumula XP para subir los 10 rangos.'}</p>
      <p style="font-size:15px;opacity:.9;margin-top:8px;line-height:1.9">${t('howXPBody')}</p>
      ${isPro() ? '' : `<p style="color:var(--gold);font-weight:700;font-size:13px">🔒 ${LANG === 'en' ? 'Ranking up requires PRO CLUB.' : 'Subir de rango requiere PRO CLUB.'}</p>`}
    </div>
    <div id="lb-panel" class="tp-card" style="text-align:left;padding:12px">
      <b style="font-size:14px;color:var(--gold)">🏆 ${LANG === 'en' ? 'LEADERBOARD — Top 10' : 'CLASIFICACIÓN — Top 10'}</b>
      <div style="opacity:.6;font-size:12px;margin-top:6px">${LANG === 'en' ? 'Loading…' : 'Cargando…'}</div>
    </div>
    </div>`;
  } else if (dashView === 'tienda') {
    const CATS = [['mesas', '🪵 Mesas', '🪵 Tables'], ['disenos', '🎨 Diseños', '🎨 Designs'], ['banderas', '🌎 Banderas', '🌎 Flags'], ['fichas', '🁢 Fichas', '🁢 Dominos'], ['extras', '✨ Extras', '✨ Extras']];
    const KIND: Record<string, string> = { mesas: 'skin', disenos: 'feltx', banderas: 'flag', fichas: 'fichas', extras: 'bling' };
    if (!KIND[dashTab]) setDashTab('mesas');
    body = `<div style="display:flex;align-items:center;gap:12px;margin-bottom:10px">
      <span style="opacity:.75;font-size:13px">${t('earnHint')}</span>
      <b style="margin-left:auto;color:var(--gold);font-size:17px;white-space:nowrap">${WALLET.coins} 🪙</b>
    </div>
    <div class="tp-tabs">${CATS.map(([id, es, en]) => `<button class="${dashTab === id ? 'active' : ''}" data-dtab="${id}">${LANG === 'en' ? en : es}</button>`).join('')}</div>
    <div class="tp-grid">${STORE.filter((i: any) => i.kind === KIND[dashTab]).map(tpItemCard).join('')}</div>`;
  } else if (dashView === 'puntos') {
    body = `<p style="opacity:.75;margin:0 0 10px">${LANG === 'en' ? '🪙 Point bundles — buy PhilCoins for tables, dominos, avatars and sobres. Bigger bundle, bigger bonus.' : '🪙 Bundles de puntos — compra PhilCoins para mesas, fichas, avatares y sobres. Más grande el bundle, más bono.'}</p>
    <div class="tp-grid">${BUNDLES.map((b: any) => {
      // shield colour = rank by value (1 = biggest bundle … 5 = smallest)
      const k = [...BUNDLES].sort((x: any, y: any) => y[3] - x[3]).findIndex((x: any) => x[0] === b[0]) + 1;
      return `<div class="tp-card">
      <div class="pt-shield"><img src="assets/rewards/rank${k}.png" alt=""><span class="pt-ico">${b[2]}</span></div>
      <b style="font-size:15px">${LANG === 'en' ? (b[6] || b[1]) : b[1]}</b>
      <div class="pt-amt">${b[3].toLocaleString()} <span class="pt-unit">🪙</span>${b[4] ? ` <span class="pt-bonus">+${b[4]}% ${L('BONO', 'BONUS')}</span>` : ''}</div>
      <button class="btn gold" data-bundle="${b[0]}">${b[5]}</button>
    </div>`;}).join('')}</div>`;
  } else if (dashView === 'pro') {
    if (!isPro()) {
      const cmp = [
        [L('🎲 Jugar vs la máquina', '🎲 Play vs the machine'), '✓', '✓'], [L('👤 Perfil + foto + equipo', '👤 Profile + photo + team'), '✓', '✓'],
        [L('💬 Chat en la galería', '💬 Chat in the gallery'), '✓', '✓'], [L('🏅 Subir de rango (10 niveles)', '🏅 Rank up (10 levels)'), '🔒', '✓'],
        [L('🎥 Video en la mesa', '🎥 Video at the table'), '🔒', '✓'], [L('💠 Drops de coleccionables', '💠 Collectible drops'), '🔒', '✓'],
        [L('🏆 Torneos', '🏆 Tournaments'), '🔒', '✓'], [L('📣 Cheerleaders en video', '📣 Cheerleaders on video'), '🔒', '✓'],
      ];
      body = `<div style="max-width:860px">
        <div class="pc-wrap">
          <section class="pc-col pc-free">
            <h3>FREE</h3>
            ${cmp.map((c: any) => `<div class="pc-row${c[1] === '🔒' ? ' pc-lock' : ''}"><span>${c[0]}</span><span class="pc-tag">${c[1]}</span></div>`).join('')}
          </section>
          <section class="pc-col pc-pro">
            <h3>PRO CLUB</h3>
            ${cmp.map((c: any) => `<div class="pc-row"><span>${c[0]}</span><span class="pc-tag">${c[2]}</span></div>`).join('')}
          </section>
        </div>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:12px">
          <div class="tp-card" style="text-align:left">
            <b style="font-size:16px">🔥 ${L('MENSUAL — $11.99/mes', 'MONTHLY — $11.99/month')}</b>
            <p style="font-size:12px;opacity:.85;line-height:1.7;margin:8px 0">
              ${L('Cada mes', 'Every month')}: <b>${L('LA MESA CALIENTE', 'THE HOT TABLE')}</b>:<br>
              🎁 ${L('1 sobre premium (Rara+ garantizada)', '1 premium pack (Rare+ guaranteed)')}<br>
              🪙 300 PhilCoins · 🎨 ${L('La Mesa Exclusiva del Mes', 'The Exclusive Table of the Month')}<br>
              🎫 ${L('25% off 1 torneo', '25% off 1 tournament')} · 📣 Cheerleaders<br>
              🔥 ${L('3 meses seguidos = pull LEGENDARIA', '3 months in a row = LEGENDARY pull')}
            </p>
            <button class="btn gold" data-pro="mensual" style="width:100%;margin:0">${L('HACERME PRO MENSUAL', 'GO PRO MONTHLY')}</button>
          </div>
          <div class="tp-card" style="text-align:left;border:2px solid var(--gold)">
            <span style="background:var(--gold);color:#241a02;font-size:10px;font-weight:900;padding:2px 8px;border-radius:8px">💥 ${L('MÁS VALOR', 'BEST VALUE')}</span>
            <b style="font-size:16px;display:block;margin-top:4px">👑 ${L('ANUAL — $99/año', 'ANNUAL — $99/year')}</b>
            <p style="font-size:12px;opacity:.85;line-height:1.7;margin:8px 0">
              ${L('Incluye <b>$100 EN VALOR</b> (¡100% de tu dinero!)', 'Includes <b>$100 IN VALUE</b> (100% of your money!)')}:<br>
              🪵 ${L('2 mesas premium a elección', '2 premium tables of your choice')} · 🁢 ${L('1 juego de fichas', '1 domino set')}<br>
              🎫 ${L('50% off tu primer torneo', '50% off your first tournament')} · 📣 Cheerleaders<br>
              + ${L('todo lo del plan mensual', 'everything in the monthly plan')}
            </p>
            <button class="btn gold" data-pro="anual" style="width:100%;margin:0">${L('HACERME PRO ANUAL', 'GO PRO ANNUAL')}</button>
          </div>
        </div>
        <p style="font-size:10px;opacity:.5;margin-top:8px">${L('Demo: los pagos reales llegan con la versión de app store.', 'Demo: real payments arrive with the app-store version.')}</p>
      </div>`;
    } else {
      const p = WALLET.pro;
      const since = new Date(p.since);
      const perksList = [
        L('🎲 Jugar vs la máquina', '🎲 Play vs the machine'), L('👤 Perfil + foto + equipo', '👤 Profile + photo + team'),
        L('💬 Chat en la galería', '💬 Chat in the gallery'), L('🏅 Subir de rango (10 niveles)', '🏅 Rank up (10 levels)'),
        L('🎥 Video en la mesa', '🎥 Video at the table'), L('💠 Drops de coleccionables', '💠 Collectible drops'),
        L('🏆 Torneos', '🏆 Tournaments'), L('📣 Cheerleaders en video', '📣 Cheerleaders on video'),
      ];
      body = `<div class="pro-royal pro-wide">
        <svg class="pr-corner tl" viewBox="0 0 50 50"><path d="M4 46 Q4 4 46 4 M12 46 Q12 12 46 12" fill="none" stroke="currentColor" stroke-width="3"/><circle cx="8" cy="8" r="4" fill="currentColor"/></svg>
        <svg class="pr-corner tr" viewBox="0 0 50 50"><path d="M4 46 Q4 4 46 4 M12 46 Q12 12 46 12" fill="none" stroke="currentColor" stroke-width="3"/><circle cx="8" cy="8" r="4" fill="currentColor"/></svg>
        <svg class="pr-corner bl" viewBox="0 0 50 50"><path d="M4 46 Q4 4 46 4 M12 46 Q12 12 46 12" fill="none" stroke="currentColor" stroke-width="3"/><circle cx="8" cy="8" r="4" fill="currentColor"/></svg>
        <svg class="pr-corner br" viewBox="0 0 50 50"><path d="M4 46 Q4 4 46 4 M12 46 Q12 12 46 12" fill="none" stroke="currentColor" stroke-width="3"/><circle cx="8" cy="8" r="4" fill="currentColor"/></svg>
        <div class="pr-head">
          <img class="pr-badge" src="assets/pro-badge.png" alt="PRO">
          <div class="pr-headtxt">
            <div class="pr-title">${L('PRO CLUB', 'PRO CLUB')}</div>
            <div class="pr-sub">${L('MIEMBRO REAL', 'ROYAL MEMBER')} · ${LANG === 'en' ? (p.plan === 'mensual' ? 'MONTHLY' : p.plan === 'anual' ? 'ANNUAL' : p.plan.toUpperCase()) : p.plan.toUpperCase()}</div>
            <div class="pr-since">${L('Miembro desde', 'Member since')} ${since.getDate()}/${since.getMonth() + 1}/${since.getFullYear()}</div>
            <span class="pr-status">● ${L('ACTIVO', 'ACTIVE')}</span>
          </div>
        </div>
        <div class="pr-div">✦</div>
        <div class="pr-perks">
          <div class="pr-perk"><div class="pr-ico">🎫</div><div class="pr-n">${WALLET.vouchers.tables}</div><div class="pr-t">${L('Vouchers de mesa', 'Table vouchers')}</div></div>
          <div class="pr-perk"><div class="pr-ico">🁢</div><div class="pr-n">${WALLET.vouchers.fichas}</div><div class="pr-t">${L('Vouchers de fichas', 'Domino-set vouchers')}</div></div>
          <div class="pr-perk"><div class="pr-ico">🏆</div><div class="pr-n">${WALLET.vouchers.t50}<small> × 50%</small></div><div class="pr-t">${L('Cupones torneo', 'Tournament coupons')} · ${WALLET.vouchers.t25 || 0} × 25%</div></div>
          <div class="pr-perk"><div class="pr-ico">📣</div><div class="pr-n">${WALLET.vouchers.cheer ? '✓' : '—'}</div><div class="pr-t">Cheerleaders</div></div>
        </div>
        <div class="pr-bene">
          <div class="pr-bene-h">${L('TUS BENEFICIOS PRO', 'YOUR PRO BENEFITS')}</div>
          ${perksList.map((x: any) => `<div class="pc-row"><span>${x}</span><span class="pc-tag">✓</span></div>`).join('')}
        </div>
        ${p.plan === 'mensual' ? `<button class="btn gold" data-drop="1" style="width:100%;margin-top:14px">🔥 ${L('RECLAMAR MESA CALIENTE DEL MES', 'CLAIM THIS MONTH\'S MESA CALIENTE')}</button>` : ''}
      </div>`;
    }
  } else if (dashView === 'coleccion') {
    body = `<div class="tp-grid">
      <div style="grid-column:1/-1;display:flex;gap:12px;align-items:center;background:var(--panel-light);border-radius:10px;padding:12px">
        <span style="font-size:40px">🎁</span>
        <div style="flex:1;min-width:0"><b>${t('colPack')}</b><br><small style="opacity:.7">${t('colHow')}</small></div>
        <button class="btn gold" data-pack="1" style="margin:0;width:auto;white-space:nowrap">${t('colOpen')} · ${PACK_PRICE} 🪙</button>
      </div>
      ${COLLECTIBLES.map((c: any) => {
        const n = WALLET.collect.filter((x: any) => x.id === c.id).length;
        const rc = RARITY[c.r][2];
        const rName = LANG === 'en' ? RARITY[c.r][1] : RARITY[c.r][0];
        return `<div class="tp-card" style="border-color:${n ? rc : '#2a3558'}">
          <div style="${n ? '' : 'filter:grayscale(1) brightness(.45)'}">${colTileHTML(c, undefined)}</div>
          <b style="font-size:12px;display:block;margin-top:6px">${n ? iname(c) : '???'}</b>
          <small style="color:${rc};display:block">${rName}${n ? ` · ×${n}` : ''}</small>
        </div>`;
      }).join('')}
      <p style="grid-column:1/-1;font-size:11px;opacity:.6;margin:4px 0 0">${t('colNote')}</p>
    </div>`;
  } else if (dashView === 'mercado') {
    const groups = COLLECTIBLES
      .map((c: any) => ({ c, copies: WALLET.collect.filter((x: any) => x.id === c.id).sort((a, b) => a.serial - b.serial) }))
      .filter((g: any) => g.copies.length);
    body = `<p style="opacity:.8;margin:0 0 4px;font-size:14px">🏪 ${LANG === 'en'
        ? 'Sell your collectibles to the market. The platform keeps a <b>10% fee</b> on every resale. Your lowest serial always stays with you.'
        : 'Vende tus coleccionables al mercado. La plataforma cobra <b>10% de fee</b> por cada reventa. Tu número de serie más bajo siempre se queda contigo.'}</p>
      <p style="opacity:.55;font-size:11px;margin:0 0 12px">${LANG === 'en'
        ? '🔗 Coming with online accounts: list at YOUR price and sell player-to-player (same 10% fee).'
        : '🔗 Con las cuentas online: publica a TU precio y vende jugador-a-jugador (mismo fee del 10%).'}</p>` +
      (groups.length ? `<div class="tp-grid">${groups.map((g: any) => {
        const price = SELL_VALUES[g.c.r];
        const fee = Math.round(price * MARKET_FEE);
        const rc = RARITY[g.c.r][2];
        return `<div class="tp-card" style="border-color:${rc}">
          ${colTileHTML(g.c, undefined)}
          <b style="font-size:12px;display:block;margin-top:6px">${iname(g.c)}</b>
          <small style="color:${rc};display:block">${g.copies.map((x: any) => '#' + String(x.serial).padStart(4, '0')).join(' · ')}</small>
          <small style="display:block;opacity:.75;margin:4px 0">${LANG === 'en' ? 'Value' : 'Valor'}: ${price} 🪙 · Fee 10%: -${fee}</small>
          <button class="btn gold" data-sell="${g.c.id}">${LANG === 'en' ? 'SELL' : 'VENDER'} → +${price - fee} 🪙</button>
        </div>`;
      }).join('')}</div>` :
      `<div class="tp-card" style="max-width:400px"><p style="opacity:.7">${LANG === 'en' ? 'No collectibles yet — win matches, rank up, or open sobres. 💠' : 'Aún no tienes coleccionables — gana partidas, sube de rango o abre sobres. 💠'}</p></div>`);
  } else if (dashView === 'compras') {
    const invCell = (it: any) => {
      const active = WALLET.active.includes(it.id);
      let sw = 'background:var(--panel)';
      if (it.kind === 'flag' || it.kind === 'feltx') sw = `background:${it.bg}`;
      else if (it.kind === 'skin') sw = `background:${(WOOD_SW as any)[it.id] || 'var(--panel)'}`;
      else if (it.kind === 'fichas') sw = `background:${it.sw}`;
      const icon = it.kind === 'bling' ? it.icon : ((it.kind === 'flag' || it.kind === 'feltx') ? it.icon : (it.kind === 'fichas' ? '🁢' : ''));
      return `<div class="inv-cell ${active ? 'on' : ''}" data-inv="${it.id}" title="${iname(it)}${active ? L(' · activo', ' · active') : ''}">
        <div class="inv-sw" style="${sw}">${icon}</div><small>${iname(it)}</small></div>`;
    };
    const owned = STORE.filter(it => WALLET.owned.includes(it.id));
    let cells = owned.map(invCell).join('');
    for (let i = owned.length; i < 12; i++) cells += '<div class="inv-cell plus" data-invplus="1">+</div>';
    body = `<b style="font-size:14px">${t('myPurch')} <small style="opacity:.6;font-weight:400">${t('tapEquip')}</small></b>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(120px,1fr));gap:10px;margin-top:10px;max-width:900px">${cells}</div>`;
  } else if (dashView === 'editar') {
    body = `<div class="tp-card tp-form" style="max-width:480px;text-align:left">
      <b style="font-size:17px">👤 ${LANG === 'en' ? 'Edit your profile' : 'Edita tu perfil'}</b>
      <div style="display:flex;align-items:center;gap:10px;margin:10px 0">
        ${PROFILE.photo ? `<img src="${PROFILE.photo}" style="width:56px;height:56px;border-radius:50%;object-fit:cover;border:2px solid var(--gold)">` : `<span style="font-size:44px">${PROFILE.avatar}</span>`}
        <b>${PROFILE.country} ${PROFILE.name || L('Jugador', 'Player')}</b>
      </div>
      <label class="btn" style="display:block;text-align:center;cursor:pointer;margin:5px 0 12px;width:100%">
        📸 ${LANG === 'en' ? 'Upload photo' : 'Subir foto'}
        <input type="file" id="dp-photo" accept="image/*" style="display:none">
      </label>
      <label>${LANG === 'en' ? 'Your name' : 'Tu nombre'}</label>
      <input id="dp-name" maxlength="14" value="${PROFILE.name || ''}">
      <label>Avatar</label>
      <select id="dp-avatar">${['🔥', '😎', '🎩', '👑', '🦁', '🐯', '💪🏽', '🎲', '🕶️', '🧔🏽'].map((a: any) => `<option ${PROFILE.avatar === a ? 'selected' : ''}>${a}</option>`).join('')}</select>
      <label>${LANG === 'en' ? 'Country' : 'País'}</label>
      <select id="dp-country">${FLAG_LIST.map((f: any) => `<option ${PROFILE.country === f[2] ? 'selected' : ''}>${f[2]}</option>`).join('')}</select>
      <div style="border-top:1px solid #232d4d;margin:14px 0 4px;padding-top:12px">
        <b style="font-size:13px">🔐 ${LANG === 'en' ? 'Account — for payments & receipts' : 'Cuenta — para pagos y recibos'}</b>
      </div>
      <label>${LANG === 'en' ? 'Full name' : 'Nombre completo'}</label>
      <input id="dp-fullname" maxlength="40" value="${ACCOUNT.fullname || ''}" placeholder="Juan Pérez">
      <label>Email</label>
      <input id="dp-email" type="email" maxlength="60" value="${ACCOUNT.email || ''}" placeholder="${L('tu@email.com', 'you@email.com')}">
      <label>${LANG === 'en' ? 'Phone (optional)' : 'Teléfono (opcional)'}</label>
      <input id="dp-phone" maxlength="20" value="${ACCOUNT.phone || ''}" placeholder="+1 …">
      <button class="btn gold" id="dp-save" style="width:100%">${LANG === 'en' ? 'SAVE PROFILE' : 'GUARDAR PERFIL'}</button>
    </div>`;
  } else if (dashView === 'equipo') {
    body = `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,480px));gap:14px;align-items:start">
    <div class="tp-card" style="grid-column:1/-1;text-align:left;display:flex;align-items:center;gap:14px;flex-wrap:wrap">
      <div class="team-tile" aria-label="Equipo"><div class="tt-top">${TEAM.flag}</div><div class="tt-split"></div><div class="tt-bot">${((TEAM.name || '').trim().split(/\s+/).map((w: string) => w[0] || '').join('').slice(0, 2).toUpperCase()) || 'EQ'}</div></div>
      <div style="flex:1;min-width:200px"><b style="font-size:20px">${TEAM.name || t('sinEquipo')}</b><br><small style="opacity:.8">${TEAM.motto ? '"' + TEAM.motto + '"' : ''}</small><br><small style="opacity:.8">${TEAM.members.length + 1} ${LANG === 'en' ? 'players' : 'jugadores'} · ${STATS.matches} ${LANG === 'en' ? 'matches' : 'partidas'}</small></div>
      <div style="text-align:center"><small style="opacity:.8">${LANG === 'en' ? 'INVITE CODE' : 'CÓDIGO DE INVITACIÓN'}</small><br><b style="font-size:20px;letter-spacing:3px;color:#8a5a10">${teamCode()}</b></div>
    </div>
    <div class="tp-card tp-form" style="text-align:left">
      <b style="font-size:16px">👥 ${LANG === 'en' ? 'Your team' : 'Tu equipo'}</b>
      <label style="margin-top:10px;display:block">${LANG === 'en' ? 'Team name' : 'Nombre del equipo'}</label>
      <input id="de-name" maxlength="16" value="${TEAM.name || ''}" placeholder="${L('Los Tigres del Malecón', 'The Malecón Tigers')}">
      <label>${LANG === 'en' ? 'Team flag' : 'Bandera'}</label>
      <select id="de-flag">${FLAG_LIST.map((f: any) => `<option ${TEAM.flag === f[2] ? 'selected' : ''}>${f[2]}</option>`).join('')}</select>
      <label>${LANG === 'en' ? 'Battle cry' : 'Grito de guerra'}</label>
      <input id="de-motto" maxlength="40" value="${TEAM.motto || ''}" placeholder="${L('¡Aquí se juega con corazón!', 'Here we play with heart!')}">
      <button class="btn gold" id="de-save" style="width:100%">${LANG === 'en' ? 'SAVE TEAM' : 'GUARDAR EQUIPO'}</button>
    </div>
    <div class="tp-card tp-form" style="text-align:left">
      <b style="font-size:15px">🎽 ${LANG === 'en' ? 'Roster' : 'Plantilla'}</b>
      <div style="display:flex;align-items:center;gap:8px;padding:7px 10px;background:var(--panel-light);border-radius:8px;margin:8px 0 6px">
        ${PROFILE.photo ? `<img src="${PROFILE.photo}" style="width:28px;height:28px;border-radius:50%;object-fit:cover">` : `<span style="font-size:20px">${PROFILE.avatar}</span>`}
        <b style="flex:1;font-size:13px">${PROFILE.name || t('you')}</b>
        <small style="color:var(--gold);font-weight:800">👑 ${LANG === 'en' ? 'Captain' : 'Capitán'}</small>
      </div>
      ${TEAM.members.map((m: any, i: number) => `<div style="display:flex;align-items:center;gap:8px;padding:7px 10px;background:var(--panel-light);border-radius:8px;margin-bottom:6px">
        <span style="font-size:20px">${m.avatar}</span><b style="flex:1;font-size:13px">${m.name}</b>
        <small style="opacity:.85">${m.role}</small>
        <button class="btn" data-drm="${i}" style="margin:0;width:auto;padding:3px 9px;font-size:11px">✕</button>
      </div>`).join('')}
      ${TEAM.members.length < 5 ? `<div style="display:flex;gap:6px;margin-top:8px">
        <input id="de-mem-name" maxlength="14" placeholder="${LANG === 'en' ? 'Compay name' : 'Nombre del compay'}" style="flex:2;margin:0">
        <select id="de-mem-avatar" style="flex:0 0 60px;margin:0">${['😎', '🧢', '💪🏽', '👵🏽', '🕶️', '🎩', '💃🏽', '🧔🏽'].map((a: any) => `<option>${a}</option>`).join('')}</select>
        <select id="de-mem-role" style="flex:1;margin:0"><option>${L('🤝 Pareja', '🤝 Partner')}</option><option>${L('🔁 Suplente', '🔁 Sub')}</option><option>📣 Fan</option></select>
        <button class="btn gold" id="de-mem-add" style="margin:0;width:auto;padding:8px 12px">+</button>
      </div>` : ''}
      <div style="margin-top:12px;background:rgba(240,180,41,.1);border:1px dashed var(--gold);border-radius:10px;padding:10px;text-align:center">
        <small style="opacity:.8">${LANG === 'en' ? 'INVITE CODE' : 'CÓDIGO DE INVITACIÓN'}</small><br>
        <b style="font-size:22px;letter-spacing:4px;color:var(--gold)">${teamCode()}</b>
        <button class="btn" id="de-copy" style="width:100%;margin-top:8px">📋 ${LANG === 'en' ? 'Copy code' : 'Copiar código'}</button>
      </div>
    </div>
    </div>`;
  } else if (dashView === 'admin' && IS_FOUNDER && adminUserSel) {
    const en = LANG === 'en';
    const u = ADMIN_DB.users.find((x: any) => x.email === adminUserSel);
    if (!u) { setAdminUserSel(null); renderDash(); return; }
    const uSales = ADMIN_DB.sales.filter((s: any) => s.email === u.email);
    const fmt$ = (n: any) => '$' + n.toFixed(2);
    const fdt = (ts: any) => { const d = new Date(ts); return `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)}`; };
    const row = (l: any, v: any) => `<div style="display:flex;justify-content:space-between;gap:10px;font-size:13px;padding:7px 0;border-bottom:1px solid #232d4d"><span style="opacity:.6">${l}</span><b style="text-align:right">${v}</b></div>`;
    body = `
    <button class="btn" data-uback style="width:auto;padding:6px 14px;margin:0 0 12px">← ${en ? 'Back to Admin' : 'Volver al Admin'}</button>
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:14px;max-width:1150px;align-items:start">
      <div class="tp-card" style="text-align:left">
        <div style="display:flex;align-items:center;gap:12px">
          <span style="font-size:40px">${u.country}</span>
          <div style="flex:1">
            <b style="font-size:19px">${u.name}</b> ${u.demo ? '🧪' : '🎖️'}<br>
            <small style="color:${u.pro ? 'var(--gold)' : '#66719c'};font-weight:800">${u.pro ? '👑 PRO · ' + u.pro.toUpperCase() : 'FREE'}</small>
          </div>
          <div style="text-align:center"><div style="font-size:22px;font-weight:800;color:var(--gold)">${fmt$(u.spend || 0)}</div><small style="opacity:.6">${en ? 'lifetime' : 'de por vida'}</small></div>
        </div>
        <div style="margin-top:12px">
          ${row('📧 Email', u.email)}
          ${row(en ? '📱 Phone' : '📱 Teléfono', u.phone || '—')}
          ${row(en ? '🌎 Country' : '🌎 País', u.country)}
          ${row(en ? '🏅 Rank' : '🏅 Rango', `${RANKS[Math.max(0, Math.min(9, (u.rank || 1) - 1))][0]} ${RANKS[Math.max(0, Math.min(9, (u.rank || 1) - 1))][en ? 2 : 1]} (${u.rank}/10)`)}
          ${row(en ? '📅 Joined' : '📅 Se unió', fdt(u.joined))}
          ${row(en ? '🛒 Purchases' : '🛒 Compras', uSales.length)}
        </div>
        <div style="margin-top:14px;background:var(--panel-light,rgba(255,255,255,.05));border-radius:10px;padding:11px 12px">
          <b style="font-size:13px">${en ? '💳 Payment method on file' : '💳 Método de pago registrado'}</b><br>
          <span style="font-size:14px;font-weight:700;color:var(--gold)">${payLabel(u.pay, en)}</span><br>
          <small style="opacity:.55;font-size:10.5px">${en
            ? 'PCI standard: the full card number lives in the processor vault (Stripe token) — we hold brand, last 4 and expiration. Enough to charge, refund and verify.'
            : 'Estándar PCI: el número completo vive en la bóveda del procesador (token de Stripe) — guardamos marca, últimos 4 y vencimiento. Suficiente para cobrar, reembolsar y verificar.'}</small>
        </div>
      </div>
      <div class="tp-card" style="text-align:left">
        <b>🧾 ${en ? 'Purchase history' : 'Historial de compras'}</b>
        <div style="max-height:380px;overflow:auto;margin-top:8px">
          ${uSales.map((s: any) => `<div style="display:flex;gap:8px;align-items:center;font-size:12px;padding:7px 0;border-bottom:1px solid #232d4d">
            <small style="opacity:.55;width:52px">${fdt(s.ts)}</small>
            <b style="flex:1;min-width:0">${s.item}</b>
            <small style="opacity:.75">${payLabel(s.pay, en).split(' · ')[0]}</small>
            <b style="color:var(--gold)">${fmt$(s.usd)}</b>
          </div>`).join('') || `<p style="opacity:.6;font-size:13px">${en ? 'No purchases yet.' : 'Sin compras aún.'}</p>`}
        </div>
      </div>
    </div>`;
  } else if (dashView === 'admin' && IS_FOUNDER) {
    /* live server data replaces demo rows when we're online as the Founder */
    if (typeof ONLINE_MODE !== 'undefined' && ONLINE_MODE && API_TOKEN && !(renderDash as any)._admLive) {
      (renderDash as any)._admLive = true;
      Promise.all([
        api('/api/admin/users').catch(() => null),
        api('/api/admin/sales').catch(() => null),
        api('/api/admin/chats').catch(() => null),
        api('/api/admin/errors').catch(() => null),
        api('/api/admin/resets').catch(() => null),
      ]).then(([ru, rs, rc, re, rr]) => {
        ADMIN_CHATS = (rc && rc.chats) || [];
        ADMIN_ERRORS = (re && re.errors) || [];
        ADMIN_RESETS = rr || null;
        if (ru && ru.users) {
          const svrSales = (rs && rs.sales) || [];
          ADMIN_DB.users = ru.users.map((x: any) => {
            const xp = Number(x.rank) || 0;
            let idx = 0;
            for (let i = 0; i < RANKS.length; i++) if (xp >= RANKS[i][3]) idx = i;
            return {
              name: x.fullname || x.apodo, email: x.email, phone: x.phone || '',
              country: x.country || '🌎', rank: idx + 1, pro: x.plan,
              spend: svrSales.filter((s: any) => s.email === x.email).reduce((a: any, s: any) => a + (Number(s.usd) || 0), 0),
              joined: new Date(x.joined || Date.now()).getTime(), pay: null, demo: false,
            };
          });
          ADMIN_DB.sales = svrSales.map((s: any) => ({
            item: s.item, usd: Number(s.usd) || 0, method: s.method, pay: s.pay || null,
            buyer: s.buyer, email: s.email, ts: new Date(s.ts || Date.now()).getTime(), demo: false,
          }));
          saveAdminDB();
          if (dashView === 'admin') renderDash();
        }
      });
    }
    const en = LANG === 'en';
    const users = ADMIN_DB.users, sales = ADMIN_DB.sales;
    const rev = sales.reduce((a: any, s: any) => a + s.usd, 0);
    const rev30 = sales.filter((s: any) => s.ts > Date.now() - 30 * 864e5).reduce((a: any, s: any) => a + s.usd, 0);
    const pros = users.filter((u: any) => u.pro).length;
    const conv = users.length ? Math.round(pros / users.length * 100) : 0;
    const fmt$ = (n: any) => '$' + n.toFixed(2);
    const fd = (ts: any) => { const d = new Date(ts); return `${d.getDate()}/${d.getMonth() + 1}`; };
    const kpi = (v: any, l: any, c?: any) => `<div class="tp-metric"><div class="v" style="${c ? 'color:' + c : ''}">${v}</div><div class="l">${l}</div></div>`;
    body = `
    <div style="background:rgba(240,180,41,.08);border:1px dashed var(--gold);border-radius:10px;padding:8px 12px;font-size:12px;margin-bottom:12px;max-width:1150px">
      🔧 <b>${en ? 'ADMIN — Founder only.' : 'ADMIN — Solo el Fundador.'}</b> ${en
        ? 'Rows marked 🧪 are demo data — replaced by live server data at launch. Purchases made in THIS app appear here for real.'
        : 'Las filas 🧪 son datos demo — se reemplazan con datos del servidor al lanzar. Las compras hechas en ESTA app sí aparecen aquí.'}
    </div>
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:8px;max-width:1150px;margin-bottom:14px">
      ${kpi(fmt$(rev), en ? '💰 Total revenue' : '💰 Ingresos totales', 'var(--gold)')}
      ${kpi(fmt$(rev30), en ? '📈 Last 30 days' : '📈 Últimos 30 días', 'var(--gold)')}
      ${kpi(users.length, en ? '👥 Users' : '👥 Usuarios')}
      ${kpi(pros, '👑 PRO')}
      ${kpi(conv + '%', en ? '🎯 PRO conversion' : '🎯 Conversión PRO')}
      ${kpi(fmt$(users.length ? rev / users.length : 0), '📊 ARPU')}
    </div>
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(340px,1fr));gap:14px;max-width:1150px;align-items:start">
      <div class="tp-card" style="text-align:left">
        <div style="display:flex;justify-content:space-between;align-items:center">
          <b>💵 ${en ? 'Purchases' : 'Compras'}</b>
          <button class="btn" data-csv="sales" style="margin:0;width:auto;padding:4px 10px;font-size:11px">⬇️ CSV</button>
        </div>
        <div style="max-height:350px;overflow:auto;margin-top:8px">
          ${sales.map((s: any) => `<div style="display:flex;gap:8px;align-items:center;font-size:12px;padding:6px 0;border-bottom:1px solid #232d4d">
            <small style="opacity:.55;width:36px">${fd(s.ts)}</small>
            <b style="flex:1;min-width:0">${s.item}</b>
            <small style="opacity:.7">${s.buyer}${s.demo ? ' 🧪' : ''}</small>
            <small title="${s.method}">${String(s.method).startsWith('crypto') ? '🪙' : '💳'}</small>
            <b style="color:var(--gold)">${fmt$(s.usd)}</b>
          </div>`).join('') || `<p style="opacity:.6">${en ? 'No sales yet.' : 'Sin ventas aún.'}</p>`}
        </div>
      </div>
      <div class="tp-card" style="text-align:left">
        <div style="display:flex;justify-content:space-between;align-items:center">
          <b>👥 ${en ? 'Users & collected data' : 'Usuarios y datos recopilados'}</b>
          <button class="btn" data-csv="users" style="margin:0;width:auto;padding:4px 10px;font-size:11px">⬇️ CSV</button>
        </div>
        <small style="opacity:.6;display:block;margin-top:4px">${en
          ? 'Collected at signup: full name · email · country · rank · plan · spend · join date'
          : 'Se recopila al registrarse: nombre completo · email · país · rango · plan · gasto · fecha'}</small>
        <div style="max-height:350px;overflow:auto;margin-top:8px">
          ${users.map((u: any) => `<div data-uview="${u.email}" style="display:flex;gap:8px;align-items:center;font-size:12px;padding:6px 0;border-bottom:1px solid #232d4d;cursor:pointer" title="${en ? 'Click for full profile' : 'Click para el perfil completo'}">
            <span>${u.country}</span>
            <div style="flex:1;min-width:0"><b style="color:var(--accent-light,#7ea2ff)">${u.name}${u.demo ? ' 🧪' : ' 🎖️'}</b><br><small style="opacity:.6">${u.email}</small></div>
            <small title="${en ? 'rank' : 'rango'} ${u.rank}">${RANKS[Math.max(0, Math.min(9, (u.rank || 1) - 1))][0]}</small>
            <small style="color:${u.pro ? 'var(--gold)' : '#66719c'};font-weight:800">${u.pro ? 'PRO' : 'FREE'}</small>
            <b style="width:62px;text-align:right">${fmt$(u.spend || 0)}</b>
            <span style="opacity:.5">›</span>
          </div>`).join('')}
        </div>
      </div>
    </div>
    <div class="tp-card" style="text-align:left;max-width:1150px;margin-top:14px">
      <div style="display:flex;justify-content:space-between;align-items:center">
        <b>💬 ${en ? 'Chat moderation' : 'Moderación de chat'}</b>
        <button class="btn" id="chat-refresh" style="margin:0;width:auto;padding:4px 10px;font-size:11px">🔄 ${en ? 'Refresh' : 'Actualizar'}</button>
      </div>
      <small style="opacity:.6;display:block;margin-top:4px">${en
        ? 'Every table message is stored and auto-filtered. ⚠️ = flagged by the filter. Delete a message or ban the account.'
        : 'Cada mensaje de la mesa se guarda y se auto-filtra. ⚠️ = marcado por el filtro. Borra un mensaje o suspende la cuenta.'}</small>
      <div style="max-height:340px;overflow:auto;margin-top:8px">
        ${(ADMIN_CHATS || []).map((c: any) => `<div style="display:flex;gap:8px;align-items:center;font-size:12px;padding:6px 0;border-bottom:1px solid #232d4d;${c.flagged ? 'background:rgba(224,85,85,.08)' : ''}">
          <small style="opacity:.55;width:36px">${fd(new Date(c.ts).getTime())}</small>
          <b style="color:var(--accent-light,#7ea2ff);width:90px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${c.name || (en ? 'Player' : 'Jugador')}</b>
          <span style="flex:1;min-width:0">${c.flagged ? '⚠️ ' : ''}${(c.msg || '').replace(/[<>]/g, '')}</span>
          ${c.email ? `<button class="btn" data-chatban="${c.email}" style="margin:0;width:auto;padding:3px 8px;font-size:10px;background:#7e2b2b" title="${en ? 'Ban this account' : 'Suspender cuenta'}">🚫</button>` : ''}
          <button class="btn" data-chatdel="${c.id}" style="margin:0;width:auto;padding:3px 8px;font-size:10px">🗑️</button>
        </div>`).join('') || `<p style="opacity:.6">${en ? 'No messages yet.' : 'Sin mensajes aún.'}</p>`}
      </div>
    </div>
    <div class="tp-card" style="text-align:left;max-width:1150px;margin-top:14px">
      <div style="display:flex;justify-content:space-between;align-items:center">
        <b>🔑 ${en ? 'Pending password-reset codes' : 'Códigos de recuperación pendientes'}</b>
        <button class="btn" id="reset-refresh" style="margin:0;width:auto;padding:4px 10px;font-size:11px">🔄 ${en ? 'Refresh' : 'Actualizar'}</button>
      </div>
      <small style="opacity:.6;display:block;margin-top:4px">${ADMIN_RESETS && ADMIN_RESETS.mail
        ? (en ? 'Codes are emailed automatically. If a player can\'t find the email, read the code here and send it to them (WhatsApp). Each code lasts 30 min.'
             : 'Los códigos se envían por email automáticamente. Si un jugador no encuentra el correo, léelo aquí y mándaselo (WhatsApp). Cada código vale 30 min.')
        : (en ? '⚠️ No email provider configured (RESEND_API_KEY) — players do NOT receive the code. Send it to them by hand (WhatsApp) from here. Each code lasts 30 min.'
             : '⚠️ Sin proveedor de email (RESEND_API_KEY) — los jugadores NO reciben el código. Mándaselo a mano (WhatsApp) desde aquí. Cada código vale 30 min.')}</small>
      <div style="margin-top:8px">
        ${((ADMIN_RESETS && ADMIN_RESETS.resets) || []).map((x: any) => `<div style="display:flex;gap:10px;align-items:center;font-size:12.5px;padding:7px 0;border-bottom:1px solid #232d4d">
          <b style="font-size:20px;letter-spacing:4px;color:var(--gold);cursor:pointer" data-copy="${x.code}" title="${en ? 'Copy' : 'Copiar'}">${x.code}</b>
          <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis">${(x.apodo || '').replace(/[<>]/g, '')} · ${x.email}${x.phone ? ' · 📱 ' + String(x.phone).replace(/[<>]/g, '') : ''}</span>
          <small style="opacity:.6;white-space:nowrap">${en ? 'expires in' : 'vence en'} ${Math.max(0, Math.round((x.exp - Date.now()) / 60000))} min${x.tries ? ` · ${x.tries} ${en ? 'wrong' : 'fallos'}` : ''}</small>
        </div>`).join('') || `<p style="opacity:.6">${en ? 'No pending codes.' : 'Sin códigos pendientes.'}</p>`}
      </div>
    </div>
    <div class="tp-card" style="text-align:left;max-width:1150px;margin-top:14px">
      <div style="display:flex;justify-content:space-between;align-items:center">
        <b>🐞 ${en ? 'Errors from players\' devices' : 'Errores en los dispositivos de los jugadores'}</b>
        <button class="btn" id="err-refresh" style="margin:0;width:auto;padding:4px 10px;font-size:11px">🔄 ${en ? 'Refresh' : 'Actualizar'}</button>
      </div>
      <small style="opacity:.6;display:block;margin-top:4px">${en
        ? 'Every uncaught error in the game is reported here automatically (what, where, which device, who). Newest first.'
        : 'Cada error no capturado del juego se reporta aquí automáticamente (qué, dónde, en qué dispositivo, quién). Más recientes primero.'}</small>
      <div style="max-height:340px;overflow:auto;margin-top:8px">
        ${(() => {
          // group identical messages so a crash loop reads as one row × N
          const groups: any[] = [];
          for (const e of (ADMIN_ERRORS || [])) {
            const g = groups.find((x: any) => x.msg === e.msg && x.ctx === e.ctx);
            if (g) { g.n++; continue; }
            groups.push({ ...e, n: 1 });
          }
          const dev = (ua: any) => /iPhone|iPad/.test(ua || '') ? '📱 iOS' : /Android/.test(ua || '') ? '📱 Android' : /Mobile/.test(ua || '') ? '📱' : '💻';
          return groups.map((e: any) => `<div style="font-size:12px;padding:6px 0;border-bottom:1px solid #232d4d">
            <div style="display:flex;gap:8px;align-items:center">
              <small style="opacity:.55;width:36px">${fd(new Date(e.ts || Date.now()).getTime())}</small>
              <b style="flex:1;min-width:0;color:#f0a3a3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${String(e.stack || '').replace(/"/g, '&quot;').slice(0, 1500)}">${String(e.msg || '').replace(/[<>]/g, '')}</b>
              ${e.n > 1 ? `<small style="background:#7e2b2b;border-radius:10px;padding:1px 7px;font-weight:800">×${e.n}</small>` : ''}
            </div>
            <small style="opacity:.6;display:block;margin-left:44px">${dev(e.ua)} ${e.screen || ''} · ${e.lang || ''} · ${String(e.ctx || '').replace(/[<>]/g, '')} · ${e.email || (en ? 'guest' : 'invitado')}</small>
          </div>`).join('') || `<p style="opacity:.6">${en ? 'No errors reported. 🎉' : 'Sin errores reportados. 🎉'}</p>`;
        })()}
      </div>
    </div>`;
  } else {
    const logRows = MATCH_LOG.slice(0, 12).map((m: any) => {
      const d = new Date(m.d);
      return `<div style="display:flex;align-items:center;gap:8px;padding:7px 0;border-bottom:1px solid #232d4d">
        <span class="wl ${m.won ? 'w' : 'l'}">${m.won ? L('G', 'W') : L('P', 'L')}</span>
        <b style="flex:1">${m.won ? t('victoria') : t('derrota')} ${m.a}–${m.b}${m.online ? ' <small style="opacity:.6;font-weight:600">🌐 online</small>' : ''}</b>
        <small style="opacity:.6">${d.getDate()}/${d.getMonth() + 1} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}</small>
      </div>`;
    }).join('') || `<p style="opacity:.6;font-size:13px">${t('noMatches')}</p>`;
    body = `<div class="tp-card" style="text-align:left;max-width:560px">
      <b style="font-size:15px">${t('recentMatches')}</b>
      <div style="margin-top:8px">${logRows}</div>
    </div>`;
  }

  $('dash-body').innerHTML = tabsHtml + body;
  loadLeaderboard(); // fills #lb-panel when the Rangos view is shown (Phase 3.2)

  /* wiring */
  for (const b of $$('[data-dview]')) b.onclick = () => { setDashView(b.dataset.dview); setAdminUserSel(null); renderDash(); };
  for (const b of $$('[data-uview]')) b.onclick = () => { setAdminUserSel(b.dataset.uview); renderDash(); };
  for (const b of $$('[data-uback]')) b.onclick = () => { setAdminUserSel(null); renderDash(); };
  for (const b of $$('[data-dtab]')) b.onclick = () => { setDashTab(b.dataset.dtab); renderDash(); };
  for (const b of $$('[data-tp]')) b.onclick = () => tpToggle(b.dataset.tp);
  for (const b of $$('[data-pack]')) b.onclick = () => buyPack();
  for (const b of $$('[data-pro]')) b.onclick = () => buyPro(b.dataset.pro);
  for (const b of $$('[data-bundle]')) b.onclick = () => { buyBundle(b.dataset.bundle); renderDash(); };
  for (const b of $$('[data-drop]')) b.onclick = () => { claimMonthlyDrop(undefined); renderDash(); };
  for (const b of $$('[data-sell]')) b.onclick = () => { sellCollectible(b.dataset.sell); renderDash(); };
  for (const b of $$('[data-csv]')) b.onclick = () => exportAdminCSV(b.dataset.csv);
  // chat moderation: delete a message, ban an account, or refresh the log
  const chatReload = () => api('/api/admin/chats').then((r: any) => { ADMIN_CHATS = (r && r.chats) || []; renderDash(); }).catch(() => {});
  const cr = $('chat-refresh');
  if (cr) cr.onclick = chatReload;
  const rr = $('reset-refresh');
  if (rr) rr.onclick = () => api('/api/admin/resets').then((r: any) => { ADMIN_RESETS = r || null; renderDash(); }).catch(() => {});
  for (const b of $$('[data-copy]')) b.onclick = () => {
    const code = b.dataset.copy;
    const done = () => { b.textContent = '✅ ' + code; setTimeout(() => { b.textContent = code; }, 1500); };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(done).catch(() => prompt(code, code));
    else prompt(code, code);
  };
  const er = $('err-refresh');
  if (er) er.onclick = () => api('/api/admin/errors').then((r: any) => { ADMIN_ERRORS = (r && r.errors) || []; renderDash(); }).catch(() => {});
  for (const b of $$('[data-chatdel]')) b.onclick = () =>
    api('/api/admin/chat/delete', { id: Number(b.dataset.chatdel) }).then(chatReload).catch(() => {});
  for (const b of $$('[data-chatban]')) b.onclick = () => {
    if (!confirm(LANG === 'en' ? `Ban ${b.dataset.chatban}? They won't be able to log in or chat.` : `¿Suspender a ${b.dataset.chatban}? No podrá entrar ni chatear.`)) return;
    api('/api/admin/ban', { email: b.dataset.chatban, banned: true }).then(chatReload).catch(() => {});
  };
  for (const el of $$('[data-inv]')) el.onclick = () => tpToggle(el.dataset.inv);
  for (const el of $$('[data-invplus]')) el.onclick = () => { setDashView('tienda'); renderDash(); };
  const dp = $('dash-play');
  if (dp) dp.onclick = () => $('dash-page').classList.remove('show');
  const de = $('dash-perfil');
  if (de) de.onclick = () => { setDashView('editar'); renderDash(); };
  const lo = $('dash-logout');
  if (lo) lo.onclick = doLogout;   // shared with the sidebar account menu
  const li = $('dash-login');   // guests get SIGN IN instead of LOG OUT
  if (li) li.onclick = () => renderEnroll('login');
  const chip = $('dash-pro-chip');
  if (chip) chip.onclick = () => { setDashView('pro'); renderDash(); };

  /* edit profile (in-dashboard) */
  const dpSave = $('dp-save');
  if (dpSave) {
    dpSave.onclick = () => {
      PROFILE.name = $('dp-name').value.trim();
      PROFILE.avatar = $('dp-avatar').value;
      PROFILE.country = $('dp-country').value;
      try { localStorage.setItem('profile', JSON.stringify(PROFILE)); } catch (e) {}
      const fn = $('dp-fullname'), em = $('dp-email'), ph = $('dp-phone');
      if (fn) {
        ACCOUNT.fullname = fn.value.trim();
        ACCOUNT.email = em.value.trim();
        ACCOUNT.phone = ph.value.trim();
        saveAccount();
        upsertSelfUser();
      }
      applyProfile();
      dpSave.textContent = LANG === 'en' ? '✔ SAVED!' : '✔ ¡GUARDADO!';
      dpSave.style.background = '#1d9e75';
      log(`${L('Perfil guardado', 'Profile saved')}: ${PROFILE.country} ${PROFILE.name}`, true);
      setTimeout(() => { setDashView('perfil'); renderDash(); }, 1000);
    };
    const dpPhoto = $('dp-photo');
    if (dpPhoto) dpPhoto.onchange = (ev: any) => {
      const f = ev.target.files && ev.target.files[0];
      if (!f) return;
      const rd = new FileReader();
      rd.onload = () => {
        const img = new Image();
        img.onload = () => {
          const c = document.createElement('canvas');
          c.width = c.height = 220;
          const g: any = c.getContext('2d');
          const s = Math.min(img.width, img.height);
          g.drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, 220, 220);
          PROFILE.photo = c.toDataURL('image/jpeg', 0.82);
          try { localStorage.setItem('profile', JSON.stringify(PROFILE)); } catch (e) {}
          applyProfile();
          log(L('Foto de perfil guardada 📸', 'Profile photo saved 📸'), true);
          renderDash();
        };
        img.src = rd.result as string;
      };
      rd.readAsDataURL(f);
    };
  }
  /* team (in-dashboard) */
  const deSave = $('de-save');
  if (deSave) {
    const saveTeam = () => { try { localStorage.setItem('team', JSON.stringify(TEAM)); } catch (e) {} };
    deSave.onclick = () => {
      TEAM.name = $('de-name').value.trim();
      TEAM.flag = $('de-flag').value;
      TEAM.motto = $('de-motto').value.trim();
      saveTeam();
      applyProfile();
      deSave.textContent = LANG === 'en' ? '✔ SAVED!' : '✔ ¡GUARDADO!';
      deSave.style.background = '#1d9e75';
      log(`${L('Equipo guardado', 'Team saved')}: ${TEAM.flag} ${TEAM.name}`, true);
      setTimeout(() => renderDash(), 1000);
    };
    const cp = $('de-copy');
    if (cp) cp.onclick = () => {
      const code = teamCode();
      const ok = () => { cp.textContent = LANG === 'en' ? '✅ Copied!' : '✅ ¡Copiado!'; setTimeout(() => { cp.textContent = LANG === 'en' ? '📋 Copy code' : '📋 Copiar código'; }, 2000); };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(ok).catch(() => prompt(L('Copia el código:', 'Copy the code:'), code));
      else prompt(L('Copia el código:', 'Copy the code:'), code);
    };
    const ma = $('de-mem-add');
    if (ma) ma.onclick = () => {
      const nm = $('de-mem-name').value.trim();
      if (!nm) return;
      TEAM.members.push({ name: nm, avatar: $('de-mem-avatar').value, role: $('de-mem-role').value });
      saveTeam();
      log(L(`${nm} se unió al equipo 🎽`, `${nm} joined the team 🎽`), true);
      clack();
      renderDash();
    };
    for (const rb of $$('[data-drm]')) {
      rb.onclick = () => {
        TEAM.members.splice(+rb.dataset.drm, 1);
        saveTeam();
        renderDash();
      };
    }
  }
}

export function openDash(): void {
  // the dashboard is an ACCOUNT feature: guests get the door (sign in / create) instead
  if (!ACCOUNT.email) { renderEnroll('login'); return; }
  $('dash-page').classList.add('show');
  renderDash();
}

/** Wire the dashboard page buttons (needs #dash-close, #btn-dash). */
export function initDashboard(): void {
  $('dash-close').onclick = () => $('dash-page').classList.remove('show');
  $('btn-dash').onclick = openDash;
  // 🤝 header icon → the team section (Mi Equipo)
  const bt = $('btn-team');
  if (bt) bt.onclick = () => { setDashView('equipo'); openDash(); };
}
